import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import * as Sentry from '@sentry/react';
import { useOverlayContext } from 'seagull';
import { billClientLoader, BillingType } from '~/components/BillModal/BillClientModal.types';
import { getBiCode } from '~/components/BillModal/utils/BillClientModal.utils';
import { getSubmitChargesData } from '~/components/BillModal/utils/lineItem.utils';
import { useStore, useStoreActions } from '~/contexts';
import { DEFAULT_MAX_TOTAL_SIZE } from '~/hooks/useAttachFiles';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { useBillingObservable } from '~/providers/BillingObservableProvider';
import { selectors } from '~/store/state-selectors';
import { useSubmitCharges } from '../BillClientModal/hooks/useSubmitCharges';
export const useBillClientSubmit = (param)=>{
    let { form, mode, relationshipId, now, dismissModal, isUploading, attachments, timeActivities, allowTimeActivities } = param;
    const { notifyToRefresh } = useBillingObservable();
    const overlayContext = useOverlayContext();
    const { logBI } = useStoreActions();
    const { submitCharges } = useSubmitCharges();
    const vendorId = useStore(selectors.user.businessId);
    const userId = useStore(selectors.user.id);
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: attachmentErrors } = useLocaleKeys('invoice.attachments.uploadErrors');
    const getAttachmentErrorMessage = (error)=>{
        if (error.hasServerError(ServerError.DuplicateFileNameNotAllowed)) {
            return attachmentErrors.duplicateFileName();
        }
        if (error.hasServerError(ServerError.AttachmentsTotalSizeExceeded)) {
            return attachmentErrors.totalSizeExceeded({
                maxSizeMB: DEFAULT_MAX_TOTAL_SIZE / (1024 * 1024)
            });
        }
        if (error.hasServerError(ServerError.AttachmentNotFound)) {
            return attachmentErrors.uploadFailed();
        }
        return undefined;
    };
    const sendRequest = async (data)=>{
        const { issueDate } = data;
        const biCode = getBiCode(mode, issueDate, now);
        logBI(biCode);
        const submitChargesData = getSubmitChargesData(_object_spread_props(_object_spread({}, data), {
            mode
        }), allowTimeActivities && timeActivities.hasServices() ? timeActivities : undefined, attachments.map((att)=>({
                id: att.id,
                displayName: att.displayName
            })));
        return submitCharges({
            relationshipId,
            data: submitChargesData
        });
    };
    const onSubmit = async (data)=>{
        const { items } = data;
        if (items.length === 0) return;
        const hasTimeActivity = items.some((item)=>item.type === BillingType.Service && item.timeActivityId);
        if (hasTimeActivity) logBI(12017, {
            vendorId,
            userId
        });
        overlayContext.startLoader(billClientLoader);
        const stuckTimer = setTimeout(()=>{
            var _navigator_connection;
            Sentry.captureException(new Error('BillClientModal: submit stuck loading for over 10s'), {
                extra: {
                    relationshipId,
                    vendorId,
                    userId,
                    mode,
                    itemCount: items.length,
                    isUploading,
                    formErrors: JSON.stringify(form.formState.errors),
                    navigatorOnLine: navigator.onLine,
                    connectionType: (_navigator_connection = navigator.connection) === null || _navigator_connection === void 0 ? void 0 : _navigator_connection.effectiveType
                }
            });
            overlayContext.stopLoader(billClientLoader);
        }, 10000);
        try {
            const { success, error } = await handleRequest(sendRequest(data));
            if (!success) {
                var _getAttachmentErrorMessage;
                const message = (_getAttachmentErrorMessage = getAttachmentErrorMessage(error)) !== null && _getAttachmentErrorMessage !== void 0 ? _getAttachmentErrorMessage : 'Failed to bill client';
                overlayContext.showErrorToast(message, "bill-client-".concat(mode, "-error-toast"));
            } else {
                await notifyToRefresh();
                dismissModal();
                overlayContext.showSuccessToast(commonTexts.toasts.chargesSuccessfullyCreated(), "bill-client-".concat(mode, "-success-toast"));
            }
        } finally{
            clearTimeout(stuckTimer);
            overlayContext.stopLoader(billClientLoader);
        }
    };
    return {
        onSubmit
    };
};
